import { useState } from 'react';
import styled from 'styled-components';
import { Options, components, MultiValueProps } from 'react-select';
import { Popover, PopoverBody } from 'reactstrap';
import {
  OptionTypeBase,
  SelectProps,
} from '@paradigm/design-system/src/components/Select/types';
import Select from '@paradigm/design-system/src/components/Select';
import { makeTheme } from '@paradigm/design-system/src/components/Select/styles';
import CounterpartySelector from '#/components/UnifiedRfqs/components/counterparty-selector/CounterpartySelector';
import { useCounterpartiesService } from '@paradigm/features/src/unified-rfqs/contexts/counterparties';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { mirage, paradigmLightBlue } from '@paradigm/design-system/src/colors';
import CloseIcon from '@paradigm/design-system/src/assets/CloseIcon';

interface Props {
  value: Options<OptionTypeBase>;
  onChange: (values: Options<OptionTypeBase>) => void;
}

// Custom MultiValue component that handles remove clicks
function MultiValue(props: MultiValueProps<OptionTypeBase>) {
  const { data, selectProps } = props;
  const cpsService = useCounterpartiesService();

  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Stop event propagation and prevent default behavior
    e.stopPropagation();
    e.preventDefault();

    // Find the counterparty by desk name and update its selection state
    const cp = cpsService.findByDeskName(data.value);
    if (cp) {
      cpsService.setSelected(cp.id, false);
    }

    // Let react-select handle the removal through its onChange
    if (selectProps.value && Array.isArray(selectProps.value)) {
      const newValue = selectProps.value.filter((v) => v.value !== data.value);
      selectProps.onChange(newValue, {
        action: 'remove-value',
        removedValue: data,
      });
    }
  };

  return (
    <StyledMultiValue>
      <components.MultiValueLabel {...props} />
      <RemoveButton data-remove-button="true" onClick={handleRemoveClick}>
        ×
      </RemoveButton>
    </StyledMultiValue>
  );
}

export function CounterpartySelectorPopover({ value, onChange }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  // Get design system styles
  const selectProps: SelectProps<OptionTypeBase, true> = {
    isMulti: true,
    menuIsOpen: false,
  };
  const theme = makeTheme(selectProps);

  const handleOpen = () => {
    if (!isOpen) setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <SelectContainer id="counterparty-selector" onClick={handleOpen}>
        <Select
          isMulti
          placeholder="Search..."
          onChange={onChange}
          value={value}
          menuIsOpen={false}
          theme={theme}
          components={{ MultiValue }}
        />
      </SelectContainer>
      {isMobile ? (
        <MobilePopoverContainer isOpen={isOpen}>
          <MobileHeader>
            <BackButton onClick={handleClose}>←</BackButton>
            <HeaderTitle>Select Counterparties</HeaderTitle>
          </MobileHeader>
          <MobileContent>
            <CounterpartySelector
              mode="filter"
              value={value}
              onChange={onChange}
              showContextMenu={false}
              showFilters={false}
              title="Select Counterparties"
            />
          </MobileContent>
        </MobilePopoverContainer>
      ) : (
        <StyledPopover
          target="counterparty-selector"
          placement="bottom-start"
          hideArrow
          isOpen={isOpen}
          toggle={handleClose}
          trigger="legacy"
        >
          <StyledPopoverBody>
            <Container>
              <CloseButton onClick={handleClose}>
                <CloseIcon width={15} height={15} />
              </CloseButton>
              <CounterpartySelector
                mode="filter"
                value={value}
                onChange={onChange}
                showContextMenu={false}
                showFilters={false}
                title="Select Counterparties"
              />
            </Container>
          </StyledPopoverBody>
        </StyledPopover>
      )}
    </>
  );
}

const SelectContainer = styled.div`
  cursor: pointer;
  position: relative;
`;

const StyledMultiValue = styled.div`
  display: flex;
  align-items: center;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  margin: 2px;
  padding: 4px 0px 4px 4px;
  font-size: 12px;
  opacity: 0.8;
  font-weight: 700;
`;

const RemoveButton = styled.button`
  color: ${paradigmLightBlue};
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  border-radius: 0 20px 20px 0;
  height: 100%;
  display: flex;
  align-items: center;

  &:hover {
    color: #fff;
  }
`;

const StyledPopover = styled(Popover)`
  .popover {
    background: ${mirage};
    border-radius: 7px;
    border: none;
    max-width: none;
  }
`;

const StyledPopoverBody = styled(PopoverBody)`
  background: ${mirage};
  color: white;
  padding: 0;

  input,
  button,
  [role='option'] {
    color: white;
  }

  /* Style scrollbars for better visibility */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const MobilePopoverContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${mirage};
  color: white;
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  z-index: 1;

  input,
  button,
  [role='option'] {
    color: white;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  background: ${mirage};
  z-index: 1;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  flex: 1;
  text-align: center;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  padding: 8px;
  cursor: pointer;
  color: ${paradigmLightBlue};
  margin-left: -8px;

  &:hover {
    color: white;
  }
`;

const MobileContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const CloseButton = styled.button`
  background: none;
  color: ${paradigmLightBlue};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  line-height: 1;
  width: 24px;
  height: 24px;

  &:hover {
    opacity: 1;
  }

  svg {
    color: ${paradigmLightBlue};

    &:hover {
      color: white;
    }
  }
`;

const Container = styled.div`
  position: relative;

  ${CloseButton} {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1;
    padding: 0;
  }

  h1 {
    padding-right: 40px;
  }
`;
